import { Component, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { ScreenSizeService } from "src/app/services/screen-size.service";
import {
  PaymentService,
  TransactionItem,
  TransactionListResponse,
} from "src/app/services/payment/user-wallet.service";
import { MatDialog } from "@angular/material/dialog";
import { BuyTokensComponent } from "../buy-tokens/buy-tokens.component";
import { RedeemTokensComponent } from "../redeem-tokens/redeem-tokens.component";
import { finalize, forkJoin } from "rxjs";
import { UserService } from "src/app/services/user.service";

@Component({
  selector: "app-user-wallet",
  templateUrl: "./user-wallet.component.html",
  styleUrls: ["./user-wallet.component.scss"],
})
export class UserWalletComponent implements OnInit {
  isMobile: boolean = false;
  availableBalance: string = "";
  reservedBalance: string = "";
  transactions: TransactionItem[] = [];
  error: string | null = null;
  loading = true;
  isUpdating = false; // This flag will control the "Updating wallet..." overlay
  hasAutomaticPayoutEnabled: boolean = false;

  // pagination state
  limit = 10; // items per page default
  currentPage = 1; // 1-based
  totalPages = 0;
  totalItems = 0;
  transactionsLoading = false;

  // expose Math for template usage
  public Math = Math;

  constructor(
    private router: Router,
    private screenSizeService: ScreenSizeService,
    private paymentService: PaymentService,
    private userServive: UserService,
    public dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.screenSizeService.isMobile$.subscribe((isMobile) => {
      this.isMobile = isMobile;
    });

    this.userServive.getUserUtilityConfiguration().subscribe({
      next: (response) => {
        this.hasAutomaticPayoutEnabled =
          !!response.items?.[0]?.value &&
          response.items[0].value.toLowerCase() === "true";
      },
      error: (err) => {
        console.error("Failed to fetch user utility configuration", err);
      },
    });

    const state = history.state;
    if (state && state.paymentResult === "success") {
      this.isUpdating = true;
      this.router.navigate([], { state: {}, replaceUrl: true });
      setTimeout(() => {
        this.loadWalletAndTransactions(1);
      }, 3000);
    } else {
      this.loadWalletAndTransactions(1);
    }
  }


  loadWalletAndTransactions(page = 1): void {
    this.error = null;
    if (!this.isUpdating) {
      this.loading = true;
    }
    this.transactionsLoading = true;

    const offset = (page - 1) * this.limit;

    const balance$ = this.paymentService.getWalletBalance();
    const tx$ = this.paymentService.getTransactions({ limit: this.limit, offset });

    forkJoin([balance$, tx$])
      .pipe(
        finalize(() => {
          this.loading = false;
          this.isUpdating = false;
          this.transactionsLoading = false;
        })
      )
      .subscribe({
        next: ([balanceResponse, txList]) => {

          this.availableBalance = balanceResponse.balance?.toString() ?? "0";
          this.reservedBalance = balanceResponse.reserved?.toString() ?? "0";


          this.transactions = txList.transactions ?? [];

          this.totalItems = txList.total_items ?? (this.transactions?.length ?? 0);
          this.totalPages =
            (txList.total_pages ?? Math.ceil(this.totalItems / this.limit)) || 1;
          this.currentPage =
            txList.current_page && txList.current_page > 0 ? txList.current_page : page;
        },
        error: (err) => {
          console.error("Failed to fetch wallet or transactions", err);
          this.error = "Failed to fetch wallet data";
          this.transactions = [];
          this.totalItems = 0;
          this.totalPages = 0;
        },
      });
  }

  navigateTo(path: string) {
    this.router.navigate([path]);
  }

  openBuyTokensModal(): void {
    const dialogRef = this.dialog.open(BuyTokensComponent, {});
    dialogRef.afterClosed().subscribe((result) => {
      if (result === "success") {
        this.loadWalletAndTransactions(this.currentPage);
      }
    });
  }

  openRedeemTokensModal(): void {
    const dialogRef = this.dialog.open(RedeemTokensComponent, {});
    dialogRef.afterClosed().subscribe((result) => {
      if (result === "success") {
        this.loadWalletAndTransactions(this.currentPage);
      } else if (result === "add-new") {

      }
    });
  }

  prevPage(): void {
    if (this.currentPage > 1) {
      this.loadWalletAndTransactions(this.currentPage - 1);
    }
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.loadWalletAndTransactions(this.currentPage + 1);
    }
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.loadWalletAndTransactions(page);
    }
  }

  changePageSize(evt: Event | string | number): void {

    let newLimit: number;
    if (evt instanceof Event) {
      const target = evt.target as HTMLSelectElement;
      newLimit = Number(target.value);
    } else {
      newLimit = Number(evt);
    }

    if (!isNaN(newLimit) && newLimit > 0) {
      this.limit = newLimit;
      this.loadWalletAndTransactions(1);
    }
  }

  get pages(): number[] {
    return Array.from({ length: Math.max(0, this.totalPages) }, (_, i) => i + 1);
  }
}
